import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AUTHORITY_PAGES, CASE_STUDIES } from "../../data/authorityPages";

const SITE_URL = "https://emmpieza.com";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

const organization = {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "EMMPIEZA",
    legalName: "EMMPIEZA COMUNICACIONES S.A.S",
    url: SITE_URL,
    logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
    description:
        "Empresa colombiana de inteligencia artificial, ciberseguridad y software para operaciones críticas, especializada en aviación y seguridad operacional.",
    foundingDate: "2019",
    email: "emmpieza@gmail.com",
    areaServed: { "@type": "Country", name: "Colombia" },
    subjectOf: {
        "@type": "DigitalDocument",
        name: "Boletín de registros de la Cámara de Comercio de Bogotá — agosto de 2019",
        url: "https://bibliotecadigital.ccb.org.co/server/api/core/bitstreams/88634c24-59aa-45fe-84c6-8414610c21d2/content",
    },
    knowsAbout: [
        "Inteligencia artificial",
        "Analítica de datos",
        "Desarrollo de software",
        "Ciberseguridad",
        "Aviación",
        "Seguridad operacional",
    ],
    founder: {
        "@type": "Person",
        name: "Raúl Andrés Fajardo Murillo",
        jobTitle: "Fundador y CEO",
        sameAs: "https://www.linkedin.com/in/raulandresfajardo/",
    },
};

export const SEO_PAGES = {
    "/": {
        title: "EMMPIEZA | IA, ciberseguridad y software para operaciones críticas",
        description:
            "Empresa colombiana de inteligencia artificial, ciberseguridad y software para operaciones críticas, especializada en aviación y seguridad operacional.",
    },
    "/nosotros": {
        title: "Quiénes somos | EMMPIEZA",
        description:
            "Conoce a EMMPIEZA, empresa colombiana de IA, desarrollo de software y ciberseguridad con experiencia en aviación y seguridad operacional.",
        type: "AboutPage",
    },
    "/servicios/inteligencia-artificial": {
        title: "Soluciones de inteligencia artificial y datos | EMMPIEZA",
        description:
            "Diseño de soluciones de IA, RAG, automatización, analítica predictiva y productos basados en datos para empresas.",
        type: "Service",
        serviceType: "Consultoría y desarrollo de inteligencia artificial",
    },
    "/servicios/software-y-aplicaciones": {
        title: "Desarrollo de software web y aplicaciones móviles | EMMPIEZA",
        description:
            "Desarrollo de software a medida, aplicaciones web y móviles, APIs e integraciones seguras para empresas.",
        type: "Service",
        serviceType: "Desarrollo de software y aplicaciones",
    },
    "/servicios/ciberseguridad": {
        title: "Ciberseguridad, pentesting y desarrollo seguro | EMMPIEZA",
        description:
            "Servicios de pentesting, revisión de aplicaciones, hardening y desarrollo seguro para reducir riesgo tecnológico.",
        type: "Service",
        serviceType: "Ciberseguridad y pruebas de penetración",
    },
    "/ia-aviacion-seguridad-operacional": {
        title: "IA para aviación y seguridad operacional | EMMPIEZA",
        description:
            "Inteligencia artificial y analítica de datos aplicadas a aviación, gestión del riesgo y seguridad operacional.",
        type: "Service",
        serviceType:
            "Inteligencia artificial y analítica para aviación y seguridad operacional",
    },
    "/proyectos": {
        title: "Proyectos y aplicaciones desarrolladas | EMMPIEZA",
        description:
            "Casos de desarrollo web, aplicaciones móviles, inteligencia artificial y ciberseguridad realizados por EMMPIEZA.",
        type: "CollectionPage",
    },
    "/blog": {
        title: "Blog de IA, software, ciberseguridad y aviación | EMMPIEZA",
        description:
            "Análisis sobre inteligencia artificial, desarrollo de software, ciberseguridad, aviación y seguridad operacional.",
        type: "Blog",
    },
    "/refinacv": {
        title: "RefinaCV | Analizador de hojas de vida con IA | EMMPIEZA",
        description:
            "RefinaCV analiza tu hoja de vida con inteligencia artificial: recibe en segundos una evaluación honesta de qué mejorar antes de enviarla a un reclutador.",
        type: "WebPage",
    },
    "/contacto": {
        title: "Contacto | EMMPIEZA",
        description:
            "Contacta a EMMPIEZA para proyectos de IA, software, ciberseguridad o tecnología aplicada a aviación.",
        type: "ContactPage",
    },
    "/politica-privacidad": {
        title: "Política de privacidad | EMMPIEZA",
        description: "Política de privacidad del sitio web de EMMPIEZA.",
        robots: "noindex,follow",
    },
    "/politica-datos": {
        title: "Política de tratamiento de datos | EMMPIEZA",
        description:
            "Política de tratamiento y protección de datos personales de EMMPIEZA.",
        robots: "noindex,follow",
    },
};

Object.values(AUTHORITY_PAGES).forEach((page) => {
    SEO_PAGES[page.path] = {
        title: `${page.title} | EMMPIEZA`,
        description: page.description,
        type: "TechArticle",
        citation: page.sources.map(([, url]) => url),
    };
});

Object.values(CASE_STUDIES).forEach((page) => {
    SEO_PAGES[page.path] = {
        title: `${page.title} | EMMPIEZA`,
        description: page.description,
        type: "Article",
        citation: (page.sources || []).map(([, url]) => url),
    };
});

function setMeta(selector, attribute, value) {
    let element = document.head.querySelector(selector);
    if (!element) {
        element = document.createElement("meta");
        const match = selector.match(/\[(name|property)="([^"]+)"\]/);
        if (match) element.setAttribute(match[1], match[2]);
        document.head.appendChild(element);
    }
    element.setAttribute(attribute, value);
}

function setLink(rel, href) {
    let element = document.head.querySelector(`link[rel="${rel}"]`);
    if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
    }
    element.setAttribute("href", href);
}

export function applySeo({
    title,
    description,
    path,
    type = "WebPage",
    serviceType,
    image = DEFAULT_IMAGE,
    robots = "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
    article,
    citation = [],
}) {
    const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
    document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[name="robots"]', "content", robots);
    setMeta('meta[name="author"]', "content", "EMMPIEZA");
    setLink("canonical", canonical);

    const social = {
        'meta[property="og:locale"]': "es_CO",
        'meta[property="og:type"]': article || ["Article", "TechArticle"].includes(type) ? "article" : "website",
        'meta[property="og:site_name"]': "EMMPIEZA",
        'meta[property="og:title"]': title,
        'meta[property="og:description"]': description,
        'meta[property="og:url"]': canonical,
        'meta[property="og:image"]': image,
        'meta[name="twitter:card"]': "summary_large_image",
        'meta[name="twitter:title"]': title,
        'meta[name="twitter:description"]': description,
        'meta[name="twitter:image"]': image,
    };
    Object.entries(social).forEach(([selector, value]) =>
        setMeta(selector, "content", value),
    );

    let entity;
    if (article) {
        const articleAuthor = {
            "@type": "Person",
            name: article.author || "EMMPIEZA",
            ...(article.authorProfile ? { sameAs: article.authorProfile } : {}),
            ...(article.authorRole ? { jobTitle: article.authorRole } : {}),
        };
        entity = {
            "@type": "BlogPosting",
            headline: article.headline,
            description,
            url: canonical,
            mainEntityOfPage: canonical,
            image,
            datePublished: article.datePublished,
            dateModified: article.dateModified,
            inLanguage: "es-CO",
            author: articleAuthor,
            publisher: { "@id": organization["@id"] },
            ...(article.citation?.length ? { citation: article.citation } : {}),
        };
    } else if (type === "Service") {
        entity = {
            "@type": "Service",
            name: title.split("|")[0].trim(),
            serviceType,
            description,
            url: canonical,
            provider: { "@id": organization["@id"] },
            areaServed: { "@type": "Country", name: "Colombia" },
        };
    } else {
        entity = {
            "@type": type,
            name: title,
            description,
            url: canonical,
            inLanguage: "es-CO",
            about: { "@id": organization["@id"] },
            ...(citation.length ? { citation } : {}),
            ...(["Article", "TechArticle"].includes(type) ? {
                headline: title.split("|")[0].trim(),
                author: { "@id": organization["@id"] },
                publisher: { "@id": organization["@id"] },
            } : {}),
        };
    }

    let script = document.getElementById("emmpieza-schema");
    if (!script) {
        script = document.createElement("script");
        script.id = "emmpieza-schema";
        script.type = "application/ld+json";
        document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [organization, entity],
    });
}

export function Seo(props) {
    useEffect(() => {
        applySeo(props);
    }, [props]);
    return null;
}

export default function RouteSeo() {
    const { pathname } = useLocation();
    useEffect(() => {
        const normalized = pathname.replace(/\/$/, "") || "/";
        const page = SEO_PAGES[normalized];
        if (page) applySeo({ ...page, path: normalized });
    }, [pathname]);
    return null;
}
